//* global window */
import { h } from './element';
import Suggest from './suggest';
import Datepicker from './datepicker';
import { cssPrefix } from '../config';
// import { mouseMoveUp } from '../event';
import Formula from './formula';
import { setCaretPosition, saveCaretPosition } from '../core/caret';
import { getFormulaDescription } from '../../../utils/formulaDescription';

function insertText({ target }, itxt) {
  const { value, selectionEnd } = target;
  const ntxt = `${value.slice(0, selectionEnd)}${itxt}${value.slice(selectionEnd)}`;
  target.value = ntxt;

  this.inputText = ntxt;
  this.render();

  setCaretPosition(target, selectionEnd + 1);
}

function keydownEventHandler(evt) {
  const { keyCode, altKey } = evt;
  if (keyCode !== 13 && keyCode !== 9) evt.stopPropagation();
  if (keyCode === 13 && altKey) {
    insertText.call(this, evt, '\n');
    evt.stopPropagation();
  }
  if (keyCode === 13 && !altKey) evt.preventDefault();
  // Escape key - cancel editing and revert to original value
  if (keyCode === 27) {
    this.cancel();
    evt.stopPropagation();
    evt.preventDefault();
  }
}

function inputEventHandler() {
  // save caret position
  const restore = saveCaretPosition(this.textEl.el);

  const text = this.textEl.el.textContent;
  this.inputText = text;
  // console.log(evt, 'v:', v);
  const { suggest, validator } = this;

  if (validator) {
    if (validator.type === 'list') {
      suggest.search(text);
    } else {
      suggest.hide();
    }
  } else {
    const start = text.lastIndexOf('=');
    const end = text.indexOf('(', start);
    if (start !== -1 && end !== -1) {
      const formulaName = text.substring(start + 1, end).trim();
      if (formulaName) {
        suggest.hide();
        showFormulaTooltip(formulaName);
        this.render();
        this.change('input', text);
        restore();
        return;
      }
    }
    if (start !== -1) {
      hideFormulaTooltip();
      suggest.search(text.substring(start + 1));
    } else {
      suggest.hide();
    }
  }
  this.render();
  this.change('input', text);

  // restore caret postion
  // to avoid caret postion missing when this.el.innerHTML changed
  restore();
}

function suggestItemClick(it) {
  showFormulaTooltip(it);
  const { inputText, validator } = this;
  let position = 0;
  if (validator && validator.type === 'list') {
    this.inputText = it;
    position = this.inputText.length;
  } else {
    const start = inputText.lastIndexOf('=');
    const sit = inputText.substring(0, start + 1);
    let eit = inputText.substring(start + 1);
    if (eit.indexOf(')') !== -1) {
      eit = eit.substring(eit.indexOf(')'));
    } else {
      eit = '';
    }
    this.inputText = `${sit + it}(`;
    // console.log('inputText:', this.inputText);
    position = this.inputText.length;
    this.inputText += `)${eit}`;
  }
  this.render();
  setCaretPosition(this.textEl.el, position);
}

function resetSuggestItems() {
  this.suggest.setItems(this.formulas);
  hideFormulaTooltip();
}

function showFormulaTooltip(it) {

  let existingTooltip = document.querySelector('.formula-tooltip');
  let cellElement = document.querySelector('.x-spreadsheet-editor-area');
  const tooltip = document.createElement('div');
  tooltip.classList.add('formula-tooltip');

  const tooltipContent = document.createElement('span');
  tooltipContent.classList.add('formula-tooltip-content');
  let description = getFormulaDescription(it.toUpperCase());
  tooltipContent.innerHTML = description;
  tooltip.appendChild(tooltipContent);

  if (cellElement && !existingTooltip && description) {
    cellElement.appendChild(tooltip);
  }
  else if (existingTooltip && description) {
    existingTooltip.innerHTML = '';
    existingTooltip.appendChild(tooltipContent);
  }
  else if (existingTooltip && !description) {
    existingTooltip.remove();
  }
  document.addEventListener('click', clickOutsideCellHandler);
}
function hideFormulaTooltip() {
  let existingTooltip = document.querySelector('.formula-tooltip');
  if (existingTooltip) {
    existingTooltip.remove();
  }
}

function clickOutsideCellHandler(event) {
  const isClickInsideCell = event.target.closest(`.${cssPrefix}-editor-area`) !== null;
  if (!isClickInsideCell) {
    hideFormulaTooltip();
    document.removeEventListener('click', clickOutsideCellHandler);
  }
}

function dateFormat(d) {
  let month = d.getMonth() + 1;
  let date = d.getDate();
  if (month < 10) month = `0${month}`;
  if (date < 10) date = `0${date}`;
  return `${d.getFullYear()}-${month}-${date}`;
}

export default class Editor {
  constructor(formulas, viewFn, data) {
    this.data = data;
    this.viewFn = viewFn;
    this.rowHeight = data.rows.height;
    this.formulas = formulas;
    this.suggest = new Suggest(this.formulas, (it) => {
      const unescapedKey = it.key.replace('\\.', '.');
      suggestItemClick.call(this, unescapedKey);
    });

    this.datepicker = new Datepicker();
    this.datepicker.change((d) => {
      // console.log('d:', d);
      this.setText(dateFormat(d));
      this.clear();
    });
    this.composing = false;
    this.areaEl = h('div', `${cssPrefix}-editor-area`)
      .children(
        this.textEl = h('div', 'textarea')
          .attr('contenteditable', 'true')
          .on('input', evt => inputEventHandler.call(this, evt))
          .on('paste.stop', () => { })
          .on('keydown', evt => keydownEventHandler.call(this, evt))
          .on('compositionstart.stop', () => { this.composing = true; })
          .on('compositionend.stop', () => { this.composing = false; }),
        this.textlineEl = h('div', 'textline'),
        this.suggest.el,
        this.datepicker.el,
      )
      .on('mousemove.stop', () => { })
      .on('mousedown.stop', () => { });
    this.el = h('div', `${cssPrefix}-editor`)
      .children(this.areaEl).hide();
    this.cellEl = h('div', `${cssPrefix}-formula-cell`);
    this.suggest.bindInputEvents(this.textEl);

    this.areaOffset = null;
    this.freeze = { w: 0, h: 0 };
    this.cell = null;
    this.inputText = '';
    this.originalText = ''; // Store original cell text for Escape key cancellation
    this.change = () => { };

    this.formula = new Formula(this);
  }

  setFreezeLengths(width, height) {
    this.freeze.w = width;
    this.freeze.h = height;
  }

  clear() {
    // const { cell } = this;
    // const cellText = (cell && cell.text) || '';
    
    // Excel-like behavior: if user types only "=" and moves away, clear the cell
    // A cell with only "=" is not a valid formula, so treat it as empty
    let textToSave = this.inputText;
    if (textToSave.trim() === '=') {
      textToSave = '';
    }
    
    // Always call change with 'finished' state to properly save the final value
    // This ensures that even if we're clearing the cell (empty string), 
    // it overwrites any intermediate 'input' state that was saved during typing
    if (this.inputText !== '') {
      this.change('finished', textToSave);
    }
    this.cell = null;
    this.areaOffset = null;
    this.inputText = '';
    this.originalText = '';
    this.el.hide();
    this.textEl.val('');
    this.textlineEl.html('');
    this.formula.clear();
    resetSuggestItems.call(this);
    this.datepicker.hide();
  }

  cancel() {
    // Excel-like behavior: Escape key cancels editing and reverts to original value
    // Restore the original cell text without saving any changes
    if (this.inputText !== '') {
      this.change('finished', this.originalText);
    }
    this.cell = null;
    this.areaOffset = null;
    this.inputText = '';
    this.originalText = '';
    this.el.hide();
    this.textEl.val('');
    this.textlineEl.html('');
    this.formula.clear();
    resetSuggestItems.call(this);
    this.datepicker.hide();
  }

  resetData(data) {
    this.data = data;
    this.rowHeight = data.rows.height;
  }

  setOffset(offset, suggestPosition = 'top') {
    const {
      textEl, areaEl, suggest, freeze, el, formula,
    } = this;
    if (offset) {
      this.areaOffset = offset;
      const {
        left, top, width, height, l, t,
      } = offset;
      // console.log('left:', left, ',top:', top, ', freeze:', freeze);
      const elOffset = { left: 0, top: 0 };
      // top left
      if (freeze.w > l && freeze.h > t) {
        //
      } else if (freeze.w < l && freeze.h < t) {
        elOffset.left = freeze.w;
        elOffset.top = freeze.h;
      } else if (freeze.w > l) {
        elOffset.top = freeze.h;
      } else if (freeze.h > t) {
        elOffset.left = freeze.w;
      }
      el.offset(elOffset);
      areaEl.offset({ left: left - elOffset.left - 0.8, top: top - elOffset.top - 0.8 });
      textEl.css('min-width', `${width - 9 + 0.8}px`);
      textEl.css('min-height', `${height - 3 + 0.8}px`);
      const sOffset = { left: 0 };
      sOffset[suggestPosition] = height;
      suggest.setOffset(sOffset);
      suggest.hide();
      formula.renderCells();
    }
  }

  setCell(cell, validator) {
    // console.log('::', validator);
    const { el, datepicker, suggest } = this;
    el.show();
    this.cell = cell;
    const text = (cell && cell.text) || '';
    this.originalText = text; // Store original text for cancel/revert
    this.setText(text);

    this.validator = validator;
    if (validator) {
      const { type } = validator;
      if (type === 'date') {
        datepicker.show();
        if (!/^\s*$/.test(text)) {
          datepicker.setValue(text);
        }
      }
      if (type === 'list') {
        suggest.setItems(validator.values());
        suggest.search('');
      }
    }
  }

  setText(text) {
    this.inputText = text;
    // console.log('text>>:', text);
    // firefox bug
    this.textEl.el.blur();

    this.render();
    setTimeout(() => {
      setCaretPosition(this.textEl.el, text.length);
    });
  }

  render() {
    if (this.composing) return;

    const text = this.inputText;
    if (text.length === 0) {
      hideFormulaTooltip();
    }

    if (text[0] !== '=') {
      this.textEl.html(text);
    } else {
      this.formula.render();
    }

    this.textlineEl.html(text);
  }

  formulaCellSelecting() {
    return Boolean(this.formula.cell);
  }

  formulaSelectCell(ri, ci) {
    this.formula.selectCell(ri, ci);
  }

  formulaSelectCellRange(ri, ci) {
    this.formula.selectCellRange(ri, ci);
  }
}
